import { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import api, { formatError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus } from "lucide-react";

const statusColors = {
  open: "bg-red-50 text-red-700",
  assigned: "bg-blue-50 text-blue-700",
  in_progress: "bg-amber-50 text-amber-700",
  awaiting_tenant: "bg-purple-50 text-purple-700",
  resolved: "bg-emerald-50 text-emerald-700",
  closed: "bg-gray-100 text-gray-500",
  escalated: "bg-red-50 text-red-700",
};

const categories = ["Plumbing", "Electrical", "Furniture", "Cleaning", "HVAC", "Security", "Other"];
const severities = ["low", "medium", "high", "urgent"];

export default function TenantMaintenancePage() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ category: "", severity: "medium", description: "" });

  const fetchRequests = () => {
    api.get("/maintenance")
      .then((r) => setRequests(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchRequests(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.category || !form.description) { toast.error("Fill in all required fields"); return; }
    setSubmitting(true);
    try {
      await api.post("/maintenance", form);
      toast.success("Maintenance request submitted");
      setForm({ category: "", severity: "medium", description: "" });
      setShowForm(false);
      fetchRequests();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardLayout title="Maintenance Requests">
      <div className="space-y-6" data-testid="tenant-maintenance-page">
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">Submit and track maintenance issues.</p>
          <Button onClick={() => setShowForm(!showForm)} className="bg-[#E25822] hover:bg-[#E25822]/90 text-white" data-testid="new-request-btn">
            <Plus className="h-4 w-4 mr-1" /> New Request
          </Button>
        </div>

        {showForm && (
          <Card className="border-border/60" data-testid="maintenance-form">
            <CardHeader><CardTitle className="font-heading text-lg">New Maintenance Request</CardTitle></CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Category</Label>
                    <Select value={form.category} onValueChange={(v) => setForm((p) => ({ ...p, category: v }))}>
                      <SelectTrigger className="h-11 mt-1.5" data-testid="maintenance-category-select">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Severity</Label>
                    <Select value={form.severity} onValueChange={(v) => setForm((p) => ({ ...p, severity: v }))}>
                      <SelectTrigger className="h-11 mt-1.5" data-testid="maintenance-severity-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {severities.map((s) => <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={form.description}
                    onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                    placeholder="Describe the issue in detail..."
                    className="mt-1.5 min-h-[100px]"
                    required
                    data-testid="maintenance-description-input"
                  />
                </div>
                <div className="flex gap-3">
                  <Button type="submit" className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white" disabled={submitting} data-testid="submit-maintenance-btn">
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit Request"}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Requests List */}
        <Card className="border-border/60">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
            ) : requests.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">No maintenance requests yet.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Category</TableHead>
                    <TableHead>Severity</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {requests.map((r) => (
                    <TableRow key={r._id} data-testid={`maintenance-row-${r._id}`}>
                      <TableCell className="font-medium">{r.category}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={r.severity === "urgent" ? "border-red-300 text-red-700" : r.severity === "high" ? "border-orange-300 text-orange-700" : ""}>
                          {r.severity}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate">{r.description}</TableCell>
                      <TableCell><Badge className={statusColors[r.status]}>{r.status?.replace(/_/g, " ")}</Badge></TableCell>
                      <TableCell className="text-muted-foreground text-sm">{new Date(r.created_at).toLocaleDateString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
